<script lang="ts">
  import type { Countdown } from '../lib/storage';
  import { compactTimeLeftFrom } from '../lib/countdown';
  import { colorVars } from '../lib/color';
  import { compactCountdownLabel, formatShortDueDate } from '../lib/format';
  import { href } from '../lib/router';

  type Props = {
    countdown: Countdown;
    /// false → render as a `<div>` (no link, no hover) so the same component
    /// can serve as the live preview inside the edit form without becoming
    /// tappable. Defaults to true for the Home list.
    interactive?: boolean;
    /// Home list's edit mode: card stops navigating and slides in a red
    /// minus button on the left that fires ondelete when tapped.
    editing?: boolean;
    ondelete?: () => void;
  };
  let {
    countdown,
    interactive = true,
    editing = false,
    ondelete,
  }: Props = $props();
  const tappable = $derived(interactive && !editing);

  // Cell ticks once a minute — enough for day/week/month-grained countdowns.
  // (We could go to 1s but the cell never shows seconds, so it'd waste paint.)
  let now = $state(new Date());
  $effect(() => {
    const id = setInterval(() => { now = new Date(); }, 60_000);
    return () => clearInterval(id);
  });

  const vars = $derived(colorVars(countdown.colorHex));
  const dueDate = $derived(new Date(countdown.dueDate));
  // List-level "past" matches iOS sortCountdownsFromSoonestToFurthest: only
  // flip into past once the calendar day has rolled over, so a countdown set
  // for today at 09:00 keeps reading as "Today" all afternoon instead of
  // dimming the moment its clock-time passes. The detail view stays
  // timestamp-precise (its ticking grid needs to flip at the exact dueDate).
  const isPast = $derived.by(() => {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    return dueDate < startOfToday;
  });
  const compact = $derived(
    compactTimeLeftFrom(now, dueDate, countdown.compactUnit ?? 'Day', countdown.countOnlyWeekdays ?? false),
  );
  const dueDateText = $derived(formatShortDueDate(dueDate, countdown.allDay));
</script>

<div class="row" class:editing>
  {#if editing}
    <button
      type="button"
      class="minus"
      onclick={ondelete}
      aria-label="Delete {countdown.title}"
    >
      <span class="minus-bar"></span>
    </button>
  {/if}
  <svelte:element
    this={tappable ? 'a' : 'div'}
    class="card"
    class:past={isPast}
    class:interactive={tappable}
    href={tappable ? href(`/c/${countdown.id}`) : undefined}
    style="--bg: {vars.color}; --text: {vars.textColor}; --right-bg: {vars.unitBg};"
  >
    <div class="emoji-cell">
      <span class="emoji">{countdown.emoji}</span>
    </div>
    <div class="info">
      <div class="title">{countdown.title}</div>
      <div class="due-date">{dueDateText}</div>
    </div>
    <div class="time-cell">
      {#if compact.number === 0}
        <span class="time-today">Today</span>
      {:else}
        <span class="time-value">{Math.abs(compact.number)}</span>
        <span class="time-unit">{compactCountdownLabel(compact.number, compact.unit, countdown.countOnlyWeekdays ?? false)}</span>
      {/if}
    </div>
  </svelte:element>
</div>

<style>
  /* Row wraps the card so the iOS-style edit minus can sit alongside it.
     The card itself keeps its visual identity; the minus slides in from
     the left only when editing=true (the row gets the .editing class). */
  .row {
    display: flex;
    align-items: stretch;
    gap: 10px;
  }
  .row > .card { flex: 1; min-width: 0; }

  /* Red circular minus matching the iOS UITableView delete affordance. */
  .minus {
    appearance: none;
    border: 0;
    background: #ff3b30;
    color: #fff;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    align-self: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
    transition: transform 0.1s ease;
  }
  .minus:active { transform: scale(0.92); }
  .minus-bar {
    display: block;
    width: 14px;
    height: 2.5px;
    background: #fff;
    border-radius: 2px;
  }

  /* Three-section card matching CountdownTableViewCell. Sizes ported from
     CountdownsList.storyboard (cell row height 80pt, 12pt corner radius,
     right "time" panel fixed 100pt wide, emoji column ~57pt). We use the
     same pixel values on web since the iOS cell looks the same on a
     standard iPhone screen — a faithful copy makes the lists feel familiar
     across both apps. */
  .card {
    display: grid;
    grid-template-columns: 64px 1fr 96px;
    align-items: stretch;
    background: var(--bg);
    color: var(--text);
    border-radius: 12px;
    text-decoration: none;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    min-height: 80px;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }
  .card.interactive:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.10);
  }
  .card.interactive:active { transform: translateY(0); }
  .card.past { opacity: 0.7; }

  .emoji-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 0 8px 4px;
  }
  /* 40pt to match iOS storyboard's emoji label fontDescription pointSize=40. */
  .emoji {
    font-size: 40px;
    line-height: 1;
  }

  .info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    padding: 10px 12px 10px 4px;
    min-width: 0;
  }
  /* boldSystem 22pt in the storyboard; bumped slightly via line-height so a
     short title (1 line) lines up vertically with the right panel's number. */
  .title {
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* system medium 15pt for the date label below the title. */
  .due-date {
    font-size: 0.82rem;
    font-weight: 500;
    opacity: 0.85;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Right panel uses --right-bg (same darker overlay as the detail view's
     unit cells) so the time-left column reads as a stripe inside the same
     card, not a separate element. iOS uses 100pt wide; the 96px here keeps
     the same visual weight while leaving 4px to the emoji column. */
  .time-cell {
    background: var(--right-bg);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 8px;
    text-align: center;
  }
  /* boldSystem 24pt in the storyboard ("46" / "10" / "2" digits). */
  .time-value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  /* system medium 15pt for "days left" / "weeks left" below the number. */
  .time-unit {
    font-size: 0.82rem;
    font-weight: 500;
    opacity: 0.9;
    margin-top: 4px;
    line-height: 1.15;
  }
  .time-today {
    font-size: 1.05rem;
    font-weight: 700;
  }
</style>
